import { WifiOff } from "lucide-react";
import { createContext, useContext, type ReactNode } from "react";
import { Icon } from "@/shared/ui/icon";
import { useTenantStream, type LiveState } from "./useTenantStream";

/**
 * No provider (a page rendered on its own in a test, the onboarding shell) = no live channel. The
 * no-op `subscribe` is what lets a page that reads envelopes render outside the shell without a
 * guard of its own: it simply never hears anything.
 */
const OFFLINE: LiveState = { status: "off", live: false, subscribe: () => () => {} };

const LiveContext = createContext<LiveState>(OFFLINE);

/**
 * Mounted once, by the app shell. Everything else — the live chip, the polling hooks that stand
 * down while the stream is up — reads `useLiveStatus()` (rules/01 §3, §5).
 */
export function LiveProvider({ children }: { children: ReactNode }) {
  const value = useTenantStream();
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export function useLiveStatus(): LiveState {
  return useContext(LiveContext);
}

/**
 * The one live-stream state a person has to do something about: 20 connections per organisation and
 * 3 per session, and this tab is past one of them (429 `STREAM_LIMIT`). Nothing is lost — the screens
 * fall back to polling — so this says what to close, not what has broken.
 */
export function LiveLimitBanner() {
  const { status } = useLiveStatus();
  if (status !== "limited") return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-warning/50 bg-warning-soft px-4 py-2 text-cell text-warning-fg md:px-6"
    >
      <Icon icon={WifiOff} size={16} />
      <p className="min-w-0 flex-1">
        Too many open tabs for live updates. This tab refreshes on a timer instead — close another NexBorg
        Access tab and reload this one to get live updates back.
      </p>
    </div>
  );
}
